/**
 * AI Analysis — directApiClient (LF-3214)
 */

'use client';

import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import { ActionResult } from '@/lib/types/actions';
import { describeValidationFailure } from './describe-validation-failure';
import type {
  AIAnalysisProgress,
  PreviewChangesResponse,
  ApplyChangesRequest,
  ApplyChangesResponse
} from './types';

function getStatusCode(error: unknown): number | undefined {
  if (error instanceof DirectApiError) return error.status;
  if (error && typeof error === 'object' && 'status' in error) {
    return (error as { status?: number }).status;
  }
  return undefined;
}

function messageForStatus(
  status: number | undefined,
  fallback: string
): string {
  if (status === 401) return 'Unauthorized';
  if (status === 403)
    return 'You do not have permission to perform this action';
  if (status === 404) return 'Resource not found';
  if (status === 500) return 'Server error. Please try again later';
  return fallback;
}

export async function getAnalysisProgressAction(
  matterId: number
): Promise<ActionResult<AIAnalysisProgress>> {
  try {
    const data = await directApiClient.get<AIAnalysisProgress>(
      `/matters/${matterId}/ai-analysis/progress`
    );
    return { success: true, data };
  } catch (error) {
    const status = getStatusCode(error);
    return {
      success: false,
      error: messageForStatus(
        status,
        error instanceof Error
          ? error.message
          : 'Failed to fetch analysis progress'
      )
    };
  }
}

export async function previewChangesAction(
  matterId: number,
  options: {
    maxRetries?: number;
    initialDelayMs?: number;
  } = {}
): Promise<ActionResult<PreviewChangesResponse>> {
  const { maxRetries = 5, initialDelayMs = 2000 } = options;

  try {
    let lastError: unknown = null;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const data = await directApiClient.get<PreviewChangesResponse>(
          `/matters/${matterId}/ai-analysis/preview-changes`
        );
        return { success: true, data };
      } catch (error) {
        lastError = error;
        const status = getStatusCode(error);
        if (status === 425 && attempt < maxRetries - 1) {
          const delayMs = initialDelayMs * Math.pow(2, attempt);
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          continue;
        }
        break;
      }
    }

    const status = getStatusCode(lastError);
    let errorMessage = 'Failed to fetch preview changes';
    if (status === 400) {
      errorMessage =
        'AI consolidation not completed. Please wait for analysis to finish.';
    } else if (status === 425) {
      errorMessage =
        'AI analysis is still processing. Please try again in a moment.';
    } else {
      errorMessage = messageForStatus(
        status,
        lastError instanceof Error ? lastError.message : errorMessage
      );
    }

    return { success: false, error: errorMessage };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch preview changes'
    };
  }
}

export async function applyChangesAction(
  matterId: number,
  request: ApplyChangesRequest
): Promise<ActionResult<ApplyChangesResponse>> {
  try {
    const result = await directApiClient.post<ApplyChangesResponse>(
      `/matters/${matterId}/ai-analysis/apply-changes`,
      request
    );
    return { success: true, data: result };
  } catch (error) {
    const status = getStatusCode(error);
    let errorMessage = 'Failed to apply changes';
    if (status === 400) {
      const detail =
        error instanceof DirectApiError && error.response
          ? (error.response as { message?: unknown }).message
          : undefined;
      const backendMessage = Array.isArray(detail)
        ? detail.join('. ')
        : typeof detail === 'string'
          ? detail
          : undefined;
      const rawMessage =
        backendMessage ||
        'Validation failed. Please check your data and try again';
      // VALE-1576 — name the row the failure came from. The backend returns the
      // payload path alongside `message`; without it the reviewer had a
      // constraint and no way to find what carried it.
      errorMessage =
        error instanceof DirectApiError && error.response
          ? describeValidationFailure(error.response, request, rawMessage)
          : rawMessage;
    } else {
      errorMessage = messageForStatus(
        status,
        error instanceof Error ? error.message : errorMessage
      );
    }

    return {
      success: false,
      error: errorMessage,
      ...(status ? { statusCode: status } : {})
    };
  }
}

export async function dismissAiAnalysisReviewAction(
  matterId: number
): Promise<ActionResult<void>> {
  try {
    await directApiClient.patch(
      `/matters/${matterId}/ai-analysis/dismiss-notice`
    );
    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to dismiss notice'
    };
  }
}
